
import { Component, Vue, Watch } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"

import vSelect from "vue-select"
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import InsightParameters from "@/components/Insights/InsightParameters.vue"
import TableResults from "@/components/Insights/TableResults.vue"
import ChartResults from "@/components/Insights/ChartResults.vue"
import HeatMap from "@/components/Insights/HeatMap.vue"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import CalculationTable from "@/components/Insights/CalculationTable.vue"
import RetractableSidebar from "@/components/RetractableSidebar.vue"
import InsightSettings from "@/components/Insights/InsightSettings.vue"

import { CurrentUser } from "@/types/user"
import { CaseDetails, Team } from "@/types"
import {
	Insight,
	LabelFormat,
	InsightCalculation,
	insightViews,
	DynamicParameter,
	InsightSummary,
	InsightDetails,
	createDefaultInsight
} from "@/types/insights"

import ViewFolders from "@/components/CaseList/ViewFolders.vue"
import { Clause } from "@/types/view"

import { CaseColumn, caseColumns } from "@/types/caseColumns"

import cloneDeep from "lodash/cloneDeep"

import api from "@/services/api"

// @ts-ignore
import Guid from "@/scripts/guid"
import { csvExport } from "@/scripts/csvDownload"

import { Folder, FolderType } from "@/types/folder"
import { UserColumn } from "@/types/userColumns"
import { duplicateInsight, insightDuplicationOptions, InsightDuplicationOption } from "@/types/insights/insightDuplication"
import _ from "lodash"
import { TenantSettingKeys } from "@/types/tenantSetting"
import PageWrapper from "@/components/New/Page/PageWrapper.vue"
import { FilteredCalculationResults } from "@/types/insights/calculatedResults"
import { CurrentPageDetails } from "@/types/paginatedRequest"

const setup = namespace("setup")

@Component({
	components: {
		"page": PageWrapper,
		"scrollbar": VuePerfectScrollbar,
		"parameters": InsightParameters,
		"results-table": TableResults,
		"chart-results": ChartResults,
		"heat-map": HeatMap,
		"skeleton-loader": SkeletonLoader,
		"calculation-table": CalculationTable,
		"view-folders": ViewFolders,
		"retractable-sidebar": RetractableSidebar,
		"insight-settings": InsightSettings,
		"v-select": vSelect
	}
})
export default class Insights extends Vue {
	@Getter("getMe") public me!: CurrentUser;

	@Getter("getScreenWidth") public screenWidth!: number;

	@Getter("getShouldPreventSanitizationOfRichTextFieldCSVDownloads") private preventSanitizationOfRichTextFieldCSVDownloads! : boolean;

	@setup.Getter("getTeams")
	teams!: Team[];

	@setup.Action
	loadTeams!: () => Promise<void>

	@setup.Action loadDataSources!: () => Promise<void>

	@setup.Getter("getFilterableFields")
	getFilterableFields!: CaseColumn[];

	@setup.Getter("getFilterableUserFields")
	getFilterableUserFields!: UserColumn[];

	@Getter("getTenantSettings")
	getTenantSettings!: { [ key: string]: any }

	@setup.Action("loadFields")
	loadFields!: () => Promise<void>;

	labelFormat = LabelFormat.Text;
	insights: InsightSummary[] = [];
	selectedInsight: Insight | null = null;

	queryColumns: any[] = [
		{
			key: "label",
			label: "Field"
		},
		{
			key: "operatorLabel",
			label: "Operator"
		},
		{
			key: "value",
			label: "Value"
		}
	];

	private perPage: number | string = 20
	private perPageOptions = [ 10, 20, 50, 100, 250, 500	]

	chartType: string | null = null;
	fields: any[] = [];
	userFields: UserColumn[] = [];

	columns: any[] = [];
	calculations: any[] = [];
	calculationTotals: any[] = [];

	results: any = null;
	resultGroups: any[] = [];
	private currentlyDisplayedResultGroups: FilteredCalculationResults[] = [];
	calculationGroupByLabels: any = {};

	deletingInsight: boolean = false;
	deleteConfirmInsight: any = null;
	deleteConfirmText: string | null = null;

	runningInsight: boolean = false;
	insightError: boolean = false
	insightTitle: string = "";

	folders: Folder[] = []
	parameters: DynamicParameter[] = []
	editSettings: boolean = false

	insightFilter: string | null = null

	insightToBeDuplicated: Insight | null = null
	selectedOptionsToDuplicate: string[] = []
	duplicatingInsightModalActive: boolean = false

	private currentInsight: Insight | null = null;
	private calculatedResultsId: string | null = null;
	private originalClauses: Clause[] = [];
	private selectedClauses: Clause[] | null = null;
	private loadingPage: boolean = false;

	private get hasMapPointColumn (): boolean {
		return this.columns.some(c => c && c.dataType === "map-marker")
	}

	private get validCalcGroupBy (): boolean {
		return this.calculations && this.calculations.every(c => c.groupByKeyLabels !== undefined)
	}

	private get hasResults (): boolean {
		return !!this.calculatedResultsId && this.resultGroups && this.resultGroups.length > 0 && this.resultGroups.some(group => group.results && group.results.length > 0)
	}

	private get itemsPerPage (): number {
		const perPage = parseInt(this.perPage.toString())
		return isNaN(perPage) ? 20 : perPage
	}

	@Watch("perPage")
	private onPerPageUpdated () {
		if (this.selectedInsight) {
			this.calculatedResultsId = null
			this.selectedClauses = [...this.originalClauses]
			this.selectedInsight.clauses = [...this.selectedClauses]
			this.chartType = null
			this.runInsight(this.selectedInsight)
		}
	}

	private get filteredInsights (): InsightSummary[] {
		const lowerFilter = this.insightFilter ? this.insightFilter.toLowerCase() : ""
		return this.insightFilter
			? this.insights.filter(i => i.title.toLowerCase().includes(lowerFilter))
			: this.insights
	}

	public async mounted () {
		this.insights = await api.insightsGet()

		if (this.me && this.me.editInsights) {
			this.loadFolders()
			this.loadInsightParameters()
			if (this.teams.length === 0) {
				this.loadTeams()
			}
		}

		// just get fields from Vuex
		await this.loadFields()
		this.fields = this.getFilterableFields
		this.userFields = this.getFilterableUserFields

		if (this.insights.length === 0) {
			this.newInsight()
		}

		this.loadDataSources()
	}

	// Retrieve all folders
	private async loadFolders () {
		this.folders = await api.foldersGet(FolderType.Insight)
		this.insights = await api.insightsGet()
	}

	// Retrieve all insight Parameters
	private async loadInsightParameters () {
		this.parameters = await api.insightParameters()
	}

	public async runInsight (search: Insight, pageNumber: number = 1) {
		search.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

		this.resultGroups = []
		this.columns = search.displayColumns
			? search.displayColumns!.map((c: any) => {
				const columnClone = cloneDeep(c) as any
				return columnClone
			})
			: []
		this.insightTitle = search.title

		if (!this.chartType) {
			let defaultView = search.defaultView
			if (!defaultView) {
				defaultView = "table"
			} else {
				// Ensure selected view is still applicable to this insight
				const view = insightViews.find(v => v.key === defaultView)
				if (!view || !view.canDisplay(search, search.calculations, this.fields, false)) {
					defaultView = "table"
				}
			}

			this.chartType = defaultView
		}

		if (!this.originalClauses) {
			this.originalClauses = [...search.clauses]
		}

		this.runningInsight = true
		this.insightError = false

		try {
			const calculationFields = [
				...search.calculations.map((c: any) => c.field),
				...search.calculations.map((c: any) => c.groupBy)
			].filter(f => !!f)

			search.page = pageNumber
			search.pageSize = this.itemsPerPage
			if (this.selectedClauses) {
				search.clauses = [...this.originalClauses, ...this.selectedClauses]
			}
			const groupedResults = await api.insights(search)

			let calculations = groupedResults.calculations ? groupedResults.calculations.map((calc: InsightCalculation) => {
				return {
					...calc,
					groupByKeyLabels: groupedResults.calculationGroupByLabels[calc.key]
						? groupedResults.calculationGroupByLabels[calc.key]
						: {}
				}
			}) : []

			this.calculations = calculations
			this.calculationTotals = search.calculationTotals!

			this.labelFormat = groupedResults.labelFormat
			this.resultGroups = groupedResults.results
			this.calculationGroupByLabels = groupedResults.calculationGroupByLabels
			this.currentInsight = { ...search }
			this.calculatedResultsId = groupedResults.id
		} catch (error) {
			console.error(error)
			this.insightError = true
		} finally {
			this.runningInsight = false
			this.loadingPage = false
			search.clauses = [...this.originalClauses]
		}
	}

	private newInsight () {
		this.selectedInsight = createDefaultInsight()
	}

	private async saveInsightToDbAndRefresh (insight: Insight) {
		insight.id = await api.insightSave(insight)
		this.insights = await api.insightsGet()

		if (insight.folder && insight.folder !== "") {
			if (!this.folders.find(folder => folder.title === insight.folder)) {
				this.loadFolders()
			}
		}
	}

	private async saveInsight (insight: Insight) {
		try {
			await this.saveInsightToDbAndRefresh(insight)

			this.$bvToast.toast("Insight saved", {
				title: "Saved",
				variant: "success",
				solid: true,
				autoHideDelay: 1000
			})
		} catch (err) {
			console.error(err)
			this.$bvToast.toast(err as string, {
				title: "Saving error",
				variant: "danger",
				solid: true,
				autoHideDelay: 5000
			})
		}
	}

	public get showDuplicationModal () : boolean {
		return this.getTenantSettings && this.getTenantSettings[TenantSettingKeys.ShowInsightDuplicationOptionsPopup] === true
	}

	public get doNotDuplicateInsightOptionDefaults () : InsightDuplicationOption[] {
		if (this.getTenantSettings && this.getTenantSettings[TenantSettingKeys.DoNotDuplicateInsightOptionDefaults]) {
			const optionsToDefaultIds : string[] = this.getTenantSettings[TenantSettingKeys.DoNotDuplicateInsightOptionDefaults]
			if (optionsToDefaultIds.length === 0) {
				return []
			}
			return this.insightDuplicationOptions.filter(option => optionsToDefaultIds.includes(option.id))
		}
		return []
	}

	public get selectedOptionsToNotDuplicate (): string[] {
		const selectedOptionsToNotDuplicate = this.insightDuplicationOptions
			.filter(option => !this.selectedOptionsToDuplicate.includes(option.id))
			.map(option => option.id)
		return selectedOptionsToNotDuplicate
	}

	public get insightDuplicationOptions () : InsightDuplicationOption[] {
		return insightDuplicationOptions
	}

	private async duplicateInsight (insight: Insight) {
		this.insightToBeDuplicated = insight
		this.selectedOptionsToDuplicate = _.differenceWith(insightDuplicationOptions, this.doNotDuplicateInsightOptionDefaults, (a: InsightDuplicationOption, b: InsightDuplicationOption) => a.id === b.id)
			.map(c => c.id)

		if (this.showDuplicationModal) {
			this.duplicatingInsightModalActive = true
		} else {
			await this.duplicateInsightConfirmed()
		}
	}

	private async duplicateInsightConfirmed () {
		const insightToBeDuplicated = this.insightToBeDuplicated!
		const duplicatedInsight = duplicateInsight(insightToBeDuplicated, this.selectedOptionsToNotDuplicate)
		try {
			await this.saveInsightToDbAndRefresh(duplicatedInsight)

			this.insightToBeDuplicated = null
			this.selectInsight(duplicatedInsight)

			this.$bvToast.toast(`Insight '${insightToBeDuplicated.title}' duplicated`, {
				title: "Duplicated",
				variant: "success",
				solid: true,
				autoHideDelay: 1000
			})
			this.duplicatingInsightModalActive = false
		} catch (err) {
			console.error(err)
			this.$bvToast.toast(err as string, {
				title: "Duplicate Insight error",
				variant: "danger",
				solid: true,
				autoHideDelay: 5000
			})
		}
	}

	private deleteInsight (insight: any) {
		this.deleteConfirmInsight = insight
		this.deletingInsight = true
	}

	private async deleteConfirm () {
		await api.insightDelete(this.deleteConfirmInsight.id)

		this.selectedInsight = null
		this.deleteConfirmInsight = null
		this.deletingInsight = false

		this.insights = await api.insightsGet()
	}

	private async selectInsight (insight: Insight): Promise<void> {
		this.editSettings = false

		if (this.selectedInsight === null || insight.id !== this.selectedInsight.id) {
			const insightDetails = await api.insightGet(insight.id!) as InsightDetails

			this.resultGroups = []

			// Create keys for any calculations missing a key
			if (insightDetails) {
				if (insightDetails.calculations) {
					insightDetails.calculations.forEach((calc: any) => {
						if (!calc.key) {
							calc.key = Guid.newGuid()
						}

						if (calc.expression === `{${calc.label}}` || calc.expression === "{value}") {
							// Clear expressions that do nothing
							calc.expression = null
						} else if (calc.expression) {
							calc.expression = calc.expression.replace(`{${calc.label}}`, "{value}")
						}
					})
				}
				if (insightDetails.calculationTotals) {
					insightDetails.calculationTotals.forEach((calc: any) => {
						if (!calc.key) {
							calc.key = Guid.newGuid()
						}

						if (calc.expression === `{${calc.title}}` || calc.expression === "{value}") {
							// Clear expressions that do nothing
							calc.expression = null
						} else if (calc.expression) {
							calc.expression = calc.expression.replace(`{${calc.title}}`, "{value}")
						}
					})
				}

				if (insightDetails.clauses) {
					insightDetails.clauses.forEach((clause: any) => this.setClauseFieldData(clause))
					this.originalClauses = [...insightDetails.clauses]
				}

				if (insightDetails.clauses.some(clause => clause.field === "hiddenFromInsights")) {
					// The insight has already been setup by an admin to have 'hiddenFromInsights' so they need to have the fields available.
					const hiddenFromInsightsColumn = caseColumns.find(cc => cc.key === "hiddenFromInsights")
					if (!this.fields.includes(hiddenFromInsightsColumn)) {
						this.fields.push(hiddenFromInsightsColumn)
					}
				}

				this.selectedInsight = cloneDeep(insightDetails)
				if (this.selectedInsight!.defaultView) {
					this.chartType = this.selectedInsight!.defaultView
				}

				if (this.chartType === "HeatMap") {
					if (!this.hasMapPointColumn) {
						this.chartType = null
					}
				}
			}
		}
	}

	private setClauseFieldData (clause: Clause) {
		Vue.set(clause, "fieldData", this.fields.find(f => f.key === clause.field))
		if (clause.subClauses) {
			clause.subClauses.forEach(subClause => this.setClauseFieldData(subClause))
		}
	}

	private getFieldValue (field: any, record: any, key: string, obj: any) {
		return obj
	}

	private getFieldLabel (fieldId: string, caseDetails: CaseDetails): string | null {
		if (!caseDetails.fields[fieldId]) {
			return null
		}

		return caseDetails.fields[fieldId].label
	}

	// Converts the results into a downloaded-able CSV
	private downloadResults () {
		const result = csvExport.resultsToCsv(this.columns, this.currentlyDisplayedResultGroups, this.preventSanitizationOfRichTextFieldCSVDownloads)

		const urlObject = window.URL || window
		const url = urlObject.createObjectURL(new Blob([result]))
		const el = document.createElement("a")
		const title = this.insightTitle ? this.insightTitle + ".csv" : "download.csv"
		el.setAttribute("href", url)
		el.setAttribute("download", title)
		el.click()
		urlObject.revokeObjectURL(url)
	}

	// Maps the user role field type values into a string ready for the papa-parse csv converter
	private mapUsersFromRole (record: any, users: any, hasSubRoles: boolean): string {
		try {
			if (!hasSubRoles) {
				return users.map(
					// @ts-ignore
					user => {
						let userRoleString = ""
						let usersName = record.users!.find((u: { id: any; }) => u.id === user.id)
						if (usersName) {
							userRoleString = usersName.name
						}
						if (user.timeSpent) {
							userRoleString += " (" + user.timeSpent + ")"
						}
						return userRoleString
					}
				).join(", ")
			} else {
				return users.map(
					// @ts-ignore
					subRole => {
						return subRole.title + ": " + this.mapUsersFromRole(record, subRole.users, false)
					}
				).join(". ")
			}
		} catch {
		}

		return ""
	}

	// If a value of a field is an object type, return a string value of that object
	private objectToString (object: any) {
		try {
			if (typeof (object) === "object") {
				return JSON.stringify(object)
			} else {
				return object
			}
		} catch (err) {
			return object
		}
	}

	private configureSettings () {
		this.editSettings = true
	}

	/**
	 * Clear insight folders that match deleted
	 */
	private folderDeleted (folder: Folder) {
		const folderInsights = this.insights.filter(insight => insight.folder === folder.title)
		folderInsights.forEach(insight => insight.folder = "")
	}

	private updateCurrentlyDisplayedResultGroups (resultsGroup: FilteredCalculationResults[]): void {
		this.currentlyDisplayedResultGroups = resultsGroup
	}

	private async loadNextPage (tableId: string, details: CurrentPageDetails): Promise<void> {
		if (this.currentInsight) {
			try {
				this.loadingPage = true
				this.currentInsight.page = details.pageNumber ?? 1
				this.currentInsight.pageSize = this.itemsPerPage
				this.currentInsight.tableId = tableId
				const groupedResults = await api.insightsCalculatedCasesForTable(this.currentInsight, this.calculatedResultsId ?? "")
				const toUpdate = this.resultGroups.firstOrDefault(r => r.value === tableId)

				if (!!toUpdate && !!groupedResults && !!groupedResults.results) {
					toUpdate.results = [...groupedResults.results]
				}
			} catch (err) {
				console.error(err)
				this.runInsight(this.currentInsight, this.selectedInsight?.page)
			} finally {
				this.loadingPage = false
			}
		}
	}

	private async reloadInsightBySort (details: CurrentPageDetails): Promise<void> {
		if (!!this.currentInsight && !!details && details.sortBy) {
			try {
				this.loadingPage = true
				this.currentInsight.page = details.pageNumber ?? 1
				this.currentInsight.pageSize = this.itemsPerPage
				this.currentInsight.tableId = details.value
				this.currentInsight.sortBy = details.sortBy
				this.currentInsight.sortByDescending = details.sortDesc
				this.currentInsight.sortByDataType = details.sortDataType ?? null
				const groupedResults = await api.insightsCalculatedCasesForTable(this.currentInsight, this.calculatedResultsId ?? "")
				const toUpdate = this.resultGroups.firstOrDefault(r => r.value === details.value)

				if (toUpdate) {
					toUpdate.results = [...groupedResults.results]
				}
			} catch (err) {
				console.error(err)
				this.runInsight(this.currentInsight, this.selectedInsight?.page)
			} finally {
				this.loadingPage = false
			}
		}
	}

	private async reloadInsightByClauses (clauses: Clause[]): Promise<void> {
		if (this.selectedInsight) {
			this.loadingPage = true
			this.selectedClauses = [...clauses]
			this.runInsight(this.selectedInsight)
		}
	}
}

